import { z } from "zod";
import { ChapterAudioSummarySchema, IsoDateTimeSchema, MoneySchema } from "./common";
import { BookIdSchema, ChapterIdSchema, Sha256Schema, VoiceIdSchema } from "./ids";
import { StabilitySchema, TtsModelSchema } from "./states";

/** The ElevenLabs plan's month, cached 60 s server-side; null when the account call failed. */
export const QuotaSchema = z.strictObject({
  /** Characters used this cycle. */
  characterCount: z.int().nonnegative(),
  characterLimit: z.int().nonnegative(),
  remaining: z.int().nonnegative(),
  nextResetAt: IsoDateTimeSchema.nullable(),
  tier: z.string().nullable(),
  fetchedAt: IsoDateTimeSchema,
});
export type Quota = z.infer<typeof QuotaSchema>;

export const EstimateTotalsSchema = z.strictObject({
  chapterCount: z.int().nonnegative(),
  characters: z.int().nonnegative(),
  credits: z.int().nonnegative(),
  money: MoneySchema,
  /** Projected audio length at the model's speaking rate. */
  durationMs: z.int().nonnegative(),
});
export type EstimateTotals = z.infer<typeof EstimateTotalsSchema>;

export const ChapterEstimateSchema = z.strictObject({
  chapterId: ChapterIdSchema,
  position: z.int().positive(),
  number: z.int().positive(),
  title: z.string(),
  approved: z.boolean(),
  /** Script characters of the Approved script; 0 until approved. */
  characters: z.int().nonnegative(),
  credits: z.int().nonnegative(),
  money: MoneySchema,
  audio: ChapterAudioSummarySchema.nullable(),
  /** Part of the Book render: approved and without current (non-stale) audio. */
  toRender: z.boolean(),
});
export type ChapterEstimate = z.infer<typeof ChapterEstimateSchema>;

export const ESTIMATE_BLOCKERS = ["NOT_ALL_APPROVED", "CASTING_MISSING"] as const;

/**
 * GET /books/:id/estimate. `fingerprint` is the Estimate gate: sha256 over the
 * sorted chapterId:approvedContentHash pairs, model, stability, and rate;
 * the render routes require the current one (409 ESTIMATE_STALE).
 */
export const BookEstimateSchema = z.strictObject({
  bookId: BookIdSchema,
  fingerprint: Sha256Schema,
  model: TtsModelSchema,
  stability: StabilitySchema,
  /** Credits per character for the model. */
  creditRate: z.number().positive(),
  pricePer1kCreditsCents: z.int().nonnegative(),
  chapters: z.array(ChapterEstimateSchema),
  /** The Chapters a Book render would spend on now. */
  toRender: EstimateTotalsSchema,
  /** Every approved Chapter, rendered or not. */
  approved: EstimateTotalsSchema,
  quota: QuotaSchema.nullable(),
  /** remaining >= toRender.credits; null when the quota is unknown. Informational: there is no hard cap. */
  quotaSufficient: z.boolean().nullable(),
  narrator: z.strictObject({ voiceId: VoiceIdSchema, name: z.string() }).nullable(),
  termCount: z.int().nonnegative(),
  approvedCount: z.int().nonnegative(),
  includedCount: z.int().nonnegative(),
  blockers: z.array(z.enum(ESTIMATE_BLOCKERS)),
  shownAt: IsoDateTimeSchema,
});
export type BookEstimate = z.infer<typeof BookEstimateSchema>;

/** POST /books/:id/render. */
export const BookRenderRequestSchema = z.strictObject({
  fingerprint: Sha256Schema,
  /** Chapters with Chapter Audio the Author confirmed to render again (they spend credits again). */
  confirmReRenderChapterIds: z.array(ChapterIdSchema).default([]),
});
export type BookRenderRequest = z.infer<typeof BookRenderRequestSchema>;
